import React, { useMemo, useState } from 'react';
import BookmarkViewColumn from './BookmarkViewColumn';
import DateFilter from '../DateFilter';
import FolderResults from '../../pages/Home/Search/components/SearchResults/FolderResults';
import MessageResults from '../../pages/Home/Search/components/SearchResults/MessageResults';
import useCanvasStore from '../../stores/canvasStore';
import { EMPTY_DATE_FILTER, isDateFilterActive, toDateRange } from '../../utils/dateFilter';

// Keep edits/deletes made from the folder details modal in sync across folder columns
const folderColumn = {
    results: FolderResults,
    accent: 'folders',
    itemsProp: 'folders',
    onUpdateFolder: (id, updates) => useCanvasStore.getState().updateBookmarkViewFolder(id, updates),
    onDeleteFolder: (id) => useCanvasStore.getState().removeBookmarkViewFolder(id),
};

// Columns rendered left to right; stateKey matches the list in canvasStore.
// dateFiltered columns follow the shared Date filter.
const COLUMNS = [
    {
        ...folderColumn,
        stateKey: 'bookmarkedFolders',
        title: 'Bookmarked Folders',
        emptyTitle: 'No bookmarked folders',
        emptyHint: 'Bookmark folders to see them here',
    },
    {
        ...folderColumn,
        stateKey: 'assignedFolders',
        title: 'Assigned to Me',
        dateFiltered: true,
        emptyTitle: 'No assigned folders',
        emptyHint: 'Folders assigned to you appear here',
    },
    {
        results: MessageResults,
        accent: 'messages',
        itemsProp: 'messages',
        stateKey: 'mentionedMessages',
        title: 'Mentions',
        dateFiltered: true,
        emptyTitle: 'No mentions',
        emptyHint: 'Messages and comments where you are @mentioned appear here',
    },
];

/**
 * Bookmark view: bookmarked folders, folders assigned to me, and @mentions
 * (channel messages + canvas comments), laid out like the search split view.
 * One Date filter applies to every dateFiltered column.
 */
const BookmarkView = () => {
    const [dateFilter, setDateFilter] = useState(EMPTY_DATE_FILTER);
    const dateRange = useMemo(() => toDateRange(dateFilter), [dateFilter]);

    const renderColumn = ({ dateFiltered, ...column }) => (
        <BookmarkViewColumn
            key={column.stateKey}
            {...column}
            dateRange={dateFiltered ? dateRange : undefined}
        />
    );

    // Left: unfiltered columns. Right: date-filtered columns grouped under the shared filter toolbar.
    return (
        <div className="flex-1 min-h-0 p-3 grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] grid-rows-[minmax(0,1fr)] gap-3">
            {COLUMNS.filter((column) => !column.dateFiltered).map(renderColumn)}

            <div className="min-h-0 flex flex-col gap-2.5 p-2.5 rounded-xl border border-violet-300/60 bg-violet-50/40 dark:border-violet-500/25 dark:bg-violet-500/[0.03]">
                <div className="shrink-0 flex items-center gap-2.5 px-1.5 py-0.5">
                    <DateFilter value={dateFilter} onChange={setDateFilter} />
                    <span className="text-xs text-muted-foreground">Filters Assigned to Me and Mentions</span>
                    {isDateFilterActive(dateFilter) && (
                        <button
                            type="button"
                            className="ml-auto text-xs text-violet-600 hover:text-violet-500 dark:text-violet-400 dark:hover:text-violet-300"
                            onClick={() => setDateFilter(EMPTY_DATE_FILTER)}
                        >
                            Clear
                        </button>
                    )}
                </div>
                <div className="flex-1 min-h-0 grid grid-cols-2 grid-rows-[minmax(0,1fr)] gap-2.5">
                    {COLUMNS.filter((column) => column.dateFiltered).map(renderColumn)}
                </div>
            </div>
        </div>
    );
};

export default BookmarkView;
